import { CloseOutlined, FilterOutlined } from "@ant-design/icons";
import { QueryBuilderAntD } from "@react-querybuilder/antd";
import { config } from "@src/config";
import { SupportedModelAlias } from "@src/constant/ab-testing/ab-testing-exploration.enum";
import { useGetEventParams } from "@src/hooks/use-get-event-params";
import { useGetModelMember } from "@src/hooks/use-get-model-member";
import { Button, DatePicker, Form, FormInstance, Input, Modal } from "antd";
import React from "react";
import QueryBuilder, { Field, ValueEditorProps } from "react-querybuilder";
import { MEASURE_EVENT_COUNT } from "../metric-performance/component/metric-performance-item/metric-measure";
import { CustomFieldSelector } from "../metric-performance/component/metric-performance-item/extra/custom-field-selector";
import dayjs from "dayjs";
import { CustomValueEditor } from "../metric-performance/component/metric-performance-item/extra/custom-value-editer";
import { ChartFormFieldsEnum } from "@src/pages/ab-test-explore/enum";
import { FilterTagShow } from "../metric-performance/component/metric-performance-item/visualization";
import { useABContext } from "@src/contexts/ab-testing/ab-context";

const targetedModel = SupportedModelAlias.FIREBASE;

export const getRuleFilter = (query: any): { field: string; operator: string; value: any }[] => {
  return (query?.rules ?? [])?.map((rule: any) => {
    return {
      field: rule?.field,
      operator: rule?.operator,
      value: rule?.value,
    };
  });
};
export const FunnelFilter: React.FC<{
  value?: any;
  onChange?: (value: any) => void;
}> = ({ value, onChange }) => {
  const { chartForm } = useABContext();
  const breakdownByWatch = Form.useWatch(ChartFormFieldsEnum.FUNNEL_BREAKDOWN, chartForm);

  const [query, setQuery] = React.useState<any>(value ?? config.METRIC_FILTER_DEFAULT);
  const [open, setOpen] = React.useState(false);

  const fieldOptionsWithTemplate = query?.rules?.filter((rule: any) => rule?.field?.includes("ep_")) ?? [];

  const { getAvailableDimensions, getAvailableDimensionsTree, setExcludeDimensions } = useGetModelMember();
  React.useEffect(() => {
    const fieldExclude = breakdownByWatch ? [breakdownByWatch, "event_name"] : ["event_name"];
    setExcludeDimensions(fieldExclude);
    if (value) {
      const newValues = {
        ...value,
        rules: value?.rules?.filter((rule: any) => !fieldExclude.includes(rule?.field)),
      };
      onChange?.(newValues);
      setQuery(newValues);
    }
  }, [JSON.stringify([breakdownByWatch, value])]);

  const handleCloseModal = () => {
    setOpen(false);
  };

  const availableDimensions = getAvailableDimensions({ modelAlias: "firebase" }) || [];
  const { getEventParamType, firebaseEventParams } = useGetEventParams({
    enabled: true,
  });
  const handleOk = () => {
    onChange?.(query);
    handleCloseModal();
  };

  const isRequiredEventNameInFilter = ({ targetModal, measure }: { targetModal: string; measure?: string }) => {
    return targetModal === targetedModel && measure === MEASURE_EVENT_COUNT;
  };

  const valueEditor = React.useCallback((props: ValueEditorProps) => {
    const { handleOnChange, value, operator, inputType } = props;
    if (["=", ">", "<", ">=", "<="].includes(operator) && inputType === "number") {
      return (
        <Input
          value={value}
          style={{ width: 500 }}
          placeholder="Please input value"
          onChange={(v) => {
            handleOnChange(v.target.value);
          }}
        />
      );
    }
    if (operator === "between" && inputType === "date") {
      return (
        <DatePicker.RangePicker
          value={Array.isArray(value) ? value.map((v) => (dayjs(v).isValid() ? dayjs(v) : null)) : (null as any)}
          style={{ width: 500 }}
          onChange={(v, dateString) => {
            handleOnChange(dateString);
          }}
        />
      );
    }
    if (operator === "is_set" || operator === "is_not_set") {
      return null;
    }
    return <CustomValueEditor {...props} />;
  }, []);

  const quantityOfFilter = query?.rules?.length ?? 0;
  return (
    <div className="">
      <div className="flex items-center">
        <span className="text-sm ">Filter : </span>
        <Button
          icon={<FilterOutlined />}
          type="link"
          onClick={() => {
            setOpen(true);
          }}
        >
          Add Filter ({quantityOfFilter})
        </Button>
      </div>

      <Modal
        open={open}
        closeIcon={<CloseOutlined onClick={handleCloseModal} className="p-2 " />}
        footer={(
          _: React.ReactNode,
          extra: {
            OkBtn: React.FC;
            CancelBtn: React.FC;
          },
        ) => {
          return (
            <div className="flex justify-end">
              <div onClick={handleCloseModal}>
                <extra.CancelBtn />
              </div>
              <div className="mx-1" />
              <div>
                <Button type="primary" onClick={handleOk}>
                  Save
                </Button>
              </div>
            </div>
          );
        }}
        onCancel={handleCloseModal}
        onOk={handleOk}
        width={800}
        title="Funnel filter"
        centered
      >
        <QueryBuilderAntD>
          <QueryBuilder
            query={query}
            fields={availableDimensions
              .map((r) => {
                const { name, field_type, matched_regex, options } = r;
                const toBuilderType = (t?: string) => {
                  if (!t) return "text";
                  else if (t.toLowerCase() === "string") return "text";
                  else if (t.toLowerCase() === "int64" || t.toLowerCase() === "float64") return "number";
                  else if (t.toLowerCase() === "date") return "date";
                  else return "text";
                };

                const inputType = toBuilderType(field_type);
                return {
                  name,
                  label: name,
                  matched_regex: matched_regex,
                  inputType,
                  required: Boolean(options?.filter?.required),
                };
              })
              .concat(
                fieldOptionsWithTemplate?.map((item: any) => ({
                  name: item.field,
                  label: item.field,
                  inputType: getEventParamType(item.field?.replace("ep_", "")) ?? "text",
                  required: false,
                })),
              )}
            getOperators={(field: string, misc: { fieldData: Field }) => {
              // dynamic member currently does not has enough meta to detect supported operators
              if (misc.fieldData === undefined) return [{ name: "=", label: "=" }];

              const { inputType } = misc.fieldData;

              const getOperators = (t?: string) => {
                const _operators = [
                  { name: "is_set", label: "Is set" },
                  { name: "is_not_set", label: "Is not set" },
                  { name: "=", label: "=" },
                ];
                if (t === "text") {
                  //skip
                } else if (t === "date") _operators.push(...[{ name: "between", label: "between" }]);
                else if (t === "number")
                  _operators.push(...[">", "<", ">=", "<="].map((item) => ({ name: item, label: item })));
                return _operators;
              };
              const operators = getOperators(inputType as string);
              return operators;
            }}
            controlElements={{
              fieldSelector: (props) => (
                <CustomFieldSelector
                  {...props}
                  getAvailableDimensionsTree={getAvailableDimensionsTree}
                  firebaseEventOptions={{
                    targetedModel,
                    firebaseEventParams,
                    isRequiredEventNameInFilter: () =>
                      isRequiredEventNameInFilter({ targetModal: targetedModel, measure: MEASURE_EVENT_COUNT }),
                  }}
                />
              ),
              valueEditor: valueEditor,
            }}
            controlClassnames={{
              addGroup: "hidden",
              combinators: "hidden",
            }}
            onQueryChange={(query: any) => {
              setQuery(query);
            }}
          />
        </QueryBuilderAntD>
      </Modal>
    </div>
  );
};

export const FunnelFilterShow: React.FC<{ form: FormInstance }> = ({ form }) => {
  const value = Form.useWatch(ChartFormFieldsEnum.FUNNEL_FILTER, form);
  return <FilterTagShow rules={value?.rules ?? []} />;
};
